import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { Network } from '@capacitor/network'
import { App } from '@/app'
import { APIServer } from '@/app/data/api'
import { isApp } from '@/index'
import { SQL } from '@/lib/Sqlite'
import SQLite from '../../../lib/Task/sqlite'
import { sortByKeys } from '@/app/util'
import { QualityChecklistZone, QualityChecklistItem, QualityPartner } from '../types'

// 품질점검 읽기 전용 캐시 (과업 스냅샷 체크리스트 + 협력업체)
// - 온라인: 서버 → SQLite 캐시 갱신(전체 교체) 후 메모리 적재
// - 오프라인: SQLite 캐시 → 메모리 적재
// - 쓰기(동기화 큐) 없음: 스냅샷·협력업체 편집은 총괄 콘솔(온라인) 전용
const ZONES_KEY = 'quality_checklist_zones'
const ITEMS_KEY = 'quality_checklist_items'
const PARTNERS_KEY = 'quality_partners'

export const useQualityChecklistStore = defineStore('quality_checklist', () => {
  const zones = ref<QualityChecklistZone[]>([])
  const items = ref<QualityChecklistItem[]>([])
  const partners = ref<QualityPartner[]>([])
  const currentTaskDatabaseId = ref<string>('')

  // 서버 응답을 로컬 캐시 규약(localStringId 등)에 맞게 정규화
  function normalizeRows<T extends QualityChecklistZone | QualityChecklistItem | QualityPartner>(
    rows: T[],
    taskId: string,
  ): T[] {
    return rows
      .filter(row => !row.deleted_at)
      .map(row => ({
        ...row,
        task_id: Number(taskId),
        localStringId: String(row.id),
        modifiedFromLocal: false,
      }))
  }

  /**
   * 캐시 테이블을 통째로 교체한다 (읽기 전용 캐시이므로 병합 불필요).
   * 라이브 참조로 같은 세트를 쓰는 과업들이 동일한 마스터 행(id)을 공유하므로,
   * 캐시의 PK(id)는 과업 스코프 키(`taskId_id`)로 저장해 과업 간 덮어쓰기를 방지한다.
   * (메모리·서버의 id는 원본 유지 - localStringId에 원본 id를 보존해 복원)
   * @param storeKey 대상 테이블
   * @param taskId 과업 ID
   * @param rows 서버에서 받은 최신 행 목록
   */
  async function replaceCacheTable(
    storeKey: string,
    taskId: string,
    rows: (QualityChecklistZone | QualityChecklistItem | QualityPartner)[],
  ): Promise<void> {
    try {
      const cacheRows = rows.map(
        row => ({ ...row, id: `${taskId}_${row.id}` }) as unknown as typeof row,
      )
      await SQLite.checkTableExists(storeKey, cacheRows.length > 0 ? cacheRows : undefined)
      const db = await SQL.getDatabase()
      if (SQL.createdTables.has(storeKey)) {
        await db.run(`DELETE FROM ${storeKey} WHERE task_id = ?`, [taskId])
      }
      if (cacheRows.length === 0) return
      const { itemsToProcess, columns } = await SQLite.getNeedUpdateItems(cacheRows, storeKey)
      await SQLite.updateSqliteOnly(itemsToProcess, columns, taskId, storeKey, true)
    } catch (error) {
      console.error(`[QualityChecklist] 캐시 저장 실패 (${storeKey}):`, error)
    }
  }

  // 캐시 행의 과업 스코프 id를 원본 서버 id로 복원
  function restoreCacheRows<T extends { id: number | string; localStringId?: string }>(
    rows: T[],
  ): T[] {
    return rows.map(row => ({ ...row, id: Number(row.localStringId ?? row.id) }) as T)
  }

  // SQLite 캐시에서 로드 (오프라인 경로)
  async function loadFromLocal(taskId: string): Promise<void> {
    zones.value = restoreCacheRows(
      (await SQLite.getLocalData(
        ZONES_KEY,
        'task_id',
        taskId,
        true,
      )) as unknown as QualityChecklistZone[],
    )
    items.value = restoreCacheRows(
      (await SQLite.getLocalData(
        ITEMS_KEY,
        'task_id',
        taskId,
        true,
      )) as unknown as QualityChecklistItem[],
    )
    partners.value = restoreCacheRows(
      (await SQLite.getLocalData(
        PARTNERS_KEY,
        'task_id',
        taskId,
        true,
      )) as unknown as QualityPartner[],
    )
  }

  /**
   * 과업 스냅샷 체크리스트(구역/항목) + 협력업체를 taskId 단위로 로드한다.
   * @param taskId 과업 ID
   * @param signal 취소 시그널 (페이지 이동 시 중단)
   */
  async function initData(taskId: string, signal?: AbortSignal): Promise<void> {
    currentTaskDatabaseId.value = taskId
    const isConnected = (await Network.getStatus()).connected

    if (!isConnected) {
      if (!isApp()) {
        // 웹: SQLite 캐시가 없으므로 온라인 필수
        App.toastInstance.add('error', '네트워크 연결이 필요합니다.')
        return
      }
      await loadFromLocal(taskId)
      return
    }

    try {
      const [serverZones, serverItems, serverPartners] = await Promise.all([
        APIServer.getQualityChecklistZones({ task_id: Number(taskId) }),
        APIServer.getQualityChecklistItems({ task_id: Number(taskId) }),
        APIServer.getQualityPartnerListByTaskId(Number(taskId)),
      ])
      if (signal?.aborted) return

      zones.value = normalizeRows(serverZones, taskId)
      items.value = normalizeRows(serverItems, taskId)
      partners.value = normalizeRows(serverPartners, taskId)

      if (isApp()) {
        await replaceCacheTable(ZONES_KEY, taskId, zones.value)
        await replaceCacheTable(ITEMS_KEY, taskId, items.value)
        await replaceCacheTable(PARTNERS_KEY, taskId, partners.value)
      }
    } catch (error) {
      if (signal?.aborted) return
      console.error('[QualityChecklist] 서버 로드 실패, 로컬 캐시 폴백:', error)
      if (isApp()) {
        await loadFromLocal(taskId)
      }
    }
  }

  // 구역 목록 (sort_key 정렬)
  const getZones = computed((): QualityChecklistZone[] => {
    return sortByKeys([...zones.value])
  })

  // 구역 단건 조회 (종료 과업 스냅샷은 source_zone_id로도 매칭 - 점검은 마스터 구역 id를 참조)
  const getZoneById = computed(() => {
    return (zoneId: number | string): QualityChecklistZone | undefined => {
      return (
        zones.value.find(zone => String(zone.id) === String(zoneId)) ??
        zones.value.find(
          zone => zone.source_zone_id != null && String(zone.source_zone_id) === String(zoneId),
        )
      )
    }
  })

  // 특정 구역의 항목 목록 (sort_key 정렬, 스냅샷 구역은 원본 구역 id로도 매칭)
  const getItemsByZoneId = computed(() => {
    return (zoneId: number | string): QualityChecklistItem[] => {
      const zoneIds = new Set([String(zoneId)])
      for (const zone of zones.value) {
        if (zone.source_zone_id != null && String(zone.source_zone_id) === String(zoneId)) {
          zoneIds.add(String(zone.id))
        }
      }
      return sortByKeys(items.value.filter(item => zoneIds.has(String(item.zone_id))))
    }
  })

  // 항목 단건 조회 (종료 과업 스냅샷은 source_item_id로도 매칭 - 결과는 마스터 항목 id를 참조)
  const getItemById = computed(() => {
    return (itemId: number | string): QualityChecklistItem | undefined => {
      return (
        items.value.find(item => String(item.id) === String(itemId)) ??
        items.value.find(
          item => item.source_item_id != null && String(item.source_item_id) === String(itemId),
        )
      )
    }
  })

  // 협력업체 목록 (공구 필터 지원)
  const getPartners = computed(() => {
    return (section?: string): QualityPartner[] => {
      const list = section
        ? partners.value.filter(partner => partner.section === section)
        : [...partners.value]
      return sortByKeys(list)
    }
  })

  function allClear(): void {
    zones.value = []
    items.value = []
    partners.value = []
    currentTaskDatabaseId.value = ''
  }

  return {
    zones,
    items,
    partners,
    initData,
    getZones,
    getZoneById,
    getItemsByZoneId,
    getItemById,
    getPartners,
    allClear,
  }
})
